import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Download, CheckCircle, Award, Calendar, Clock, BookOpen, Monitor, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MyCertificates = () => {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState({ presencial: [], online: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API_URL}/my-certificates`);
      setCertificates(response.data);
    } catch (error) {
      toast.error('Error al cargar los certificados');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (certId, tipo) => {
    try {
      const endpoint = tipo === 'online' 
        ? `${API_URL}/online-certificates/${certId}/download`
        : `${API_URL}/certificates/download/${certId}`;
      
      const response = await axios.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificado_${certId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Error al descargar el certificado');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const CertificateCard = ({ cert, tipo }) => (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      <div className={`h-2 ${tipo === 'online' ? 'bg-blue-500' : 'bg-green-500'}`}></div>
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${tipo === 'online' ? 'bg-blue-100' : 'bg-green-100'}`}>
              <Award className={`w-6 h-6 ${tipo === 'online' ? 'text-blue-600' : 'text-green-600'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">{cert.course?.nombre || cert.course_name}</h3>
              <Badge className={tipo === 'online' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
                {tipo === 'online' ? <Monitor className="w-3 h-3 mr-1" /> : <Users className="w-3 h-3 mr-1" />}
                {tipo === 'online' ? 'Online' : 'Presencial'}
              </Badge>
            </div>
          </div>
          <CheckCircle className="w-6 h-6 text-green-500" />
        </div>
        
        <div className="space-y-2 text-sm text-slate-600 mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Fecha: {formatDate(cert.fecha_emision || cert.created_at)}
          </div>
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Código: {cert.codigo_verificacion}
          </div>
          {cert.tiempo_total && (
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Tiempo dedicado: {Math.floor(cert.tiempo_total / 3600)}h {Math.floor((cert.tiempo_total % 3600) / 60)}m
            </div>
          )}
        </div>
        
        <Button 
          className="w-full bg-[#004E89] hover:bg-[#003B66]" 
          onClick={() => handleDownload(cert.id, tipo)}
        >
          <Download className="w-4 h-4 mr-2" />
          Descargar PDF
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  const totalCerts = certificates.presencial.length + certificates.online.length;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900">Mis Certificados</h1>
          <p className="font-inter text-slate-600 mt-1">Descarga tus certificados de formación</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Award className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalCerts}</p>
                <p className="text-sm text-slate-500">Total certificados</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{certificates.presencial.length}</p>
                <p className="text-sm text-slate-500">Presenciales</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Monitor className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{certificates.online.length}</p>
                <p className="text-sm text-slate-500">Online</p>
              </div>
            </div>
          </div>
        </div>

        {/* Certificates */}
        <Tabs defaultValue="all" className="w-full">
          <TabsList>
            <TabsTrigger value="all">Todos ({totalCerts})</TabsTrigger>
            <TabsTrigger value="presencial">Presenciales ({certificates.presencial.length})</TabsTrigger>
            <TabsTrigger value="online">Online ({certificates.online.length})</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...certificates.presencial.map(c => ({...c, _tipo: 'presencial'})), 
                ...certificates.online.map(c => ({...c, _tipo: 'online'}))]
                .map(cert => (
                  <CertificateCard key={cert.id} cert={cert} tipo={cert._tipo} />
                ))}
            </div>
            {totalCerts === 0 && (
              <div className="text-center py-12">
                <Award className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No tienes certificados aún</h3>
                <p className="text-slate-500 mt-1">Completa cursos para obtener tus certificados</p>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="presencial" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {certificates.presencial.map(cert => (
                <CertificateCard key={cert.id} cert={cert} tipo="presencial" />
              ))}
            </div>
            {certificates.presencial.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No tienes certificados presenciales</p>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="online" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {certificates.online.map(cert => (
                <CertificateCard key={cert.id} cert={cert} tipo="online" />
              ))}
            </div>
            {certificates.online.length === 0 && (
              <div className="text-center py-12">
                <Monitor className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No tienes certificados online</p>
                <p className="text-sm text-slate-400 mt-1">Completa un curso online al 100% para obtenerlo</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default MyCertificates;
